import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Select, { StylesConfig } from 'react-select';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import { FaChevronDown } from 'react-icons/fa';

// Mirrors every field emp_te_checklist.fmt lets a user Enter-Query on, across BOTH of its blocks -
// TE_CHECKLIST_OFFICE (the Offices grid) and TE_CHECKLIST_STATS (the Time Entries grid). Confirmed
// directly against the running Oracle app (every column in both grids is queryable there), not
// guessed from the .fmt alone. "Type" is deliberately omitted - every row already shares the same
// lm_type as the page's fixed Employee/Contractor context, so searching it would always match
// every row.
export interface ITimeAndExpenseChecklistSearchCriteria {
    periodNo: number;
    // Offices grid
    office?: string;
    landmenCount?: number;
    entered?: number;
    invoiced?: number;
    proof?: number;
    paid?: number;
    // Time Entries grid
    employeeNumber?: number;
    name?: string;
    active?: boolean;
    turnkeyProspects?: number;
    approvalCount?: number;
    approvedCount?: number;
    approvalsNeeded?: number;
    lastDate?: string;
    invoiceDate?: string;
    proofDate?: string;
    fileDate?: string;
    checkDate?: string;
    receipts?: number;
    days?: number;
    services?: number;
    expenses?: number;
    total?: number;
}

interface IOptionType {
    value: string;
    label: string;
}

interface ITimeAndExpenseChecklistSearchProps {
    selectedPayperiod?: string;
    setSelectedPayPeriod: (value: string) => void;
    setPayPeriodSearchTerm: (value: string) => void;
    payPeriods: Array<{ id: number | string; name: string }>;
    onAdvancedSearch: (criteria: Omit<ITimeAndExpenseChecklistSearchCriteria, 'periodNo'>) => void;
    // 'C' (Contractor, Project Billing's own entry point) or 'E' (Employee, Employee Payroll's Emp.
    // Checklist button) - defaults per how the page was invoked, switchable here.
    employeeType: string;
    setEmployeeType: (value: string) => void;
}

const EMPLOYEE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'C', label: 'Contractor' },
    { value: 'E', label: 'Employee' },
];

// Matches the plain RHFInputBox `.field` look, same as EmployeePayrollSearch/CheckRegisterSearch's
// own Selects.
const selectStyles: StylesConfig<IOptionType, false> = {
    control: (provided) => ({
        ...provided,
        minHeight: '3rem',
        height: '3rem',
        borderRadius: 0,
        border: 'none',
        borderBottom: '1px solid grey',
        boxShadow: 'none',
    }),
    valueContainer: (provided) => ({ ...provided, height: '3rem', padding: '0px 8px' }),
    input: (provided) => ({ ...provided, margin: '0px', padding: '0px' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '3rem' }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '0px' }),
    clearIndicator: (provided) => ({ ...provided, padding: '0px' }),
};

const ACTIVE_OPTIONS: Array<{ value: 'Y' | 'N'; label: string }> = [
    { value: 'Y', label: 'Active' },
    { value: 'N', label: 'Inactive' },
];

const ADVANCED_FIELD_NAMES = [
    'officeSearch', 'landmenCountSearch', 'enteredSearch', 'invoicedSearch', 'proofSearch', 'paidSearch',
    'employeeNumberSearch', 'nameSearch', 'turnkeyProspectsSearch', 'approvalCountSearch', 'approvedCountSearch',
    'approvalsNeededSearch', 'lastDateSearch', 'invoiceDateSearch', 'proofDateSearch', 'fileDateSearch',
    'checkDateSearch', 'receiptsSearch', 'daysSearch', 'servicesSearch', 'expensesSearch', 'totalSearch',
];

// Period selection stays a simple, always-visible bar (mirrors :control.period_no - the one real
// search field the Oracle CONTROL block has), now with its own Search/Clear/Minimize alongside it
// (matches EmployeePayrollSearch's own main-bar layout). Every other field either grid exposes to
// Enter-Query lives in a floating "Advanced Search" panel that slides down OVER the grids below
// (position: absolute, not part of normal flow) rather than pushing them down - toggled open/closed
// by the Advanced Search button and auto-closed the moment Search runs.
//
// The floating panel's fields stay mounted at all times (hidden only via opacity/max-height, never
// conditional JSX) - react-hook-form's uncontrolled inputs lose their typed value the instant their
// DOM node unmounts, so conditionally rendering them would silently wipe every filter on close.
// Values only reset when Clear is clicked.
const TimeAndExpenseChecklistSearch = (props: ITimeAndExpenseChecklistSearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
    const [activeStatus, setActiveStatus] = useState<'Y' | 'N' | undefined>(undefined);

    const periodOptions: Array<IOptionType> = (props.payPeriods ?? []).map((pp) => ({ value: pp.id.toString(), label: pp.name }));
    const selectedPeriodOption = periodOptions.find((o) => o.label.replace(/-/g, '') === props.selectedPayperiod) ?? null;

    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    const executeSearch = () => {
        const values = getValues();
        props.onAdvancedSearch({
            office: values.officeSearch || undefined,
            landmenCount: toNumber(values.landmenCountSearch),
            entered: toNumber(values.enteredSearch),
            invoiced: toNumber(values.invoicedSearch),
            proof: toNumber(values.proofSearch),
            paid: toNumber(values.paidSearch),
            employeeNumber: toNumber(values.employeeNumberSearch),
            name: values.nameSearch || undefined,
            active: activeStatus === 'Y' ? true : activeStatus === 'N' ? false : undefined,
            turnkeyProspects: toNumber(values.turnkeyProspectsSearch),
            approvalCount: toNumber(values.approvalCountSearch),
            approvedCount: toNumber(values.approvedCountSearch),
            approvalsNeeded: toNumber(values.approvalsNeededSearch),
            lastDate: values.lastDateSearch || undefined,
            invoiceDate: values.invoiceDateSearch || undefined,
            proofDate: values.proofDateSearch || undefined,
            fileDate: values.fileDateSearch || undefined,
            checkDate: values.checkDateSearch || undefined,
            receipts: toNumber(values.receiptsSearch),
            days: toNumber(values.daysSearch),
            services: toNumber(values.servicesSearch),
            expenses: toNumber(values.expensesSearch),
            total: toNumber(values.totalSearch),
        });
        setIsAdvancedOpen(false);
    };

    const clearSearchFilters = () => {
        ADVANCED_FIELD_NAMES.forEach((field) => setValue(field, ''));
        setActiveStatus(undefined);
        props.setSelectedPayPeriod(undefined);
    };

    const fieldStyle = { width: '100%' };

    const renderField = (label: string, name: string, type: 'text' | 'number' | 'date' = 'number', width = '11%') => (
        <div style={{ width, paddingLeft: '4px' }}>
            <Fieldset label={label} style={{ border: 0, padding: 0 }}>
                <RHFInputBox name={name} type={type} register={register} errors={errors} style={fieldStyle} bodyStyle={fieldStyle} />
            </Fieldset>
        </div>
    );

    const isAdvancedVisible = isAdvancedOpen && !isCollapsed;

    return (
        <div style={{ position: 'relative' }}>
            {isCollapsed ? (
                <div style={{ textAlign: 'center' }}>
                    <Button
                        text="(+) Search Filters"
                        className="blue-hover-button"
                        style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                        onClick={() => setIsCollapsed(false)}
                    />
                </div>
            ) : (
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        <div style={{ maxWidth: '96rem', margin: '0 auto' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Time & Expense Checklist Search">
                                <form>
                                    <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                        <div style={{ width: '15rem', flexShrink: 0 }}>
                                            <Fieldset label="Pay Period" style={{ border: 0, padding: 0 }}>
                                                <Select
                                                    isClearable={true}
                                                    inputId="timeAndExpenseChecklistPeriodSearchInput"
                                                    placeholder="Pay Period"
                                                    options={periodOptions}
                                                    value={selectedPeriodOption}
                                                    onChange={(val: IOptionType | null) => props.setSelectedPayPeriod(val?.value)}
                                                    onInputChange={(inputText) => props.setPayPeriodSearchTerm(inputText)}
                                                    isSearchable={true}
                                                    styles={selectStyles}
                                                />
                                            </Fieldset>
                                        </div>
                                        <div style={{ paddingLeft: '1rem', flexShrink: 0 }}>
                                            <Fieldset label="Employee Type" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                                {EMPLOYEE_TYPE_OPTIONS.map((option) => (
                                                    <span key={option.value} style={{ marginLeft: '.25rem' }}>
                                                        <input
                                                            type="radio"
                                                            name="teChecklistEmployeeType"
                                                            id={`teChecklistEmployeeType-${option.value}`}
                                                            checked={props.employeeType === option.value}
                                                            onChange={() => props.setEmployeeType(option.value)}
                                                        />
                                                        <label htmlFor={`teChecklistEmployeeType-${option.value}`} style={{ marginLeft: '.15rem' }}>{option.label}</label>
                                                    </span>
                                                ))}
                                            </Fieldset>
                                        </div>
                                        <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                            <Button
                                                text="Search"
                                                className="blue-hover-button"
                                                style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                onClick={() => executeSearch()}
                                            />
                                            <Button
                                                text="(-) Minimize"
                                                className="blue-hover-button"
                                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                onClick={() => setIsCollapsed(true)}
                                            />
                                            <Button
                                                text="Clear"
                                                className="blue-hover-button"
                                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                                onClick={() => clearSearchFilters()}
                                            />
                                            <Button
                                                text="Advanced Search"
                                                reactIcon={FaChevronDown}
                                                iconStyle={{
                                                    fontSize: '1.6rem',
                                                    marginRight: '.5rem',
                                                    transform: `scaleY(${isAdvancedOpen ? -1 : 1})`,
                                                    transition: 'transform .25s ease',
                                                    verticalAlign: 'middle',
                                                }}
                                                className="blue-hover-button"
                                                style={{
                                                    whiteSpace: 'nowrap', marginLeft: '1rem', width: '14rem', height: '3rem',
                                                    verticalAlign: 'middle', borderRadius: '5px',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                                onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </Panel>
                        </Panel>
                        </div>
                    </Page.Section>
                </Page>
            )}

            {/* Floating "Advanced Search" panel - always mounted (see note above on why), positioned
                to float over whatever is below rather than push it down, and purely CSS-toggled. */}
            <div
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 40,
                    overflow: 'hidden',
                    maxHeight: isAdvancedVisible ? '2000px' : '0px',
                    opacity: isAdvancedVisible ? 1 : 0,
                    pointerEvents: isAdvancedVisible ? 'auto' : 'none',
                    transition: 'max-height .35s ease, opacity .25s ease',
                    boxShadow: '0 8px 16px rgba(0,0,0,.3)',
                }}
            >
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={12}>
                        <div style={{ maxWidth: '96rem', margin: '0 auto' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="Advanced Search">
                                <form>
                                    <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                        <span style={{ width: '100%', fontWeight: 'bold', paddingLeft: '4px', paddingBottom: '.25rem' }}>Offices</span>
                                        {renderField('Office', 'officeSearch', 'text')}
                                        {renderField('LM #', 'landmenCountSearch')}
                                        {renderField('Entered', 'enteredSearch')}
                                        {renderField('Invoice', 'invoicedSearch')}
                                        {renderField('Proof', 'proofSearch')}
                                        {renderField('Paid', 'paidSearch')}
                                        <div style={{ paddingLeft: '4px', flexShrink: 0 }}>
                                            <Fieldset label="Active?" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', whiteSpace: 'nowrap', width: 'max-content' }}>
                                                <span style={{ marginLeft: '.25rem' }}>
                                                    <input
                                                        type="radio"
                                                        name="teChecklistActiveSearch"
                                                        id="teChecklistActiveSearch-any"
                                                        checked={activeStatus === undefined}
                                                        onChange={() => setActiveStatus(undefined)}
                                                    />
                                                    <label htmlFor="teChecklistActiveSearch-any" style={{ marginLeft: '.15rem' }}>Any</label>
                                                </span>
                                                {ACTIVE_OPTIONS.map((option) => (
                                                    <span key={option.value} style={{ marginLeft: '.25rem' }}>
                                                        <input
                                                            type="radio"
                                                            name="teChecklistActiveSearch"
                                                            id={`teChecklistActiveSearch-${option.value}`}
                                                            checked={activeStatus === option.value}
                                                            onChange={() => setActiveStatus(option.value)}
                                                        />
                                                        <label htmlFor={`teChecklistActiveSearch-${option.value}`} style={{ marginLeft: '.15rem' }}>{option.label}</label>
                                                    </span>
                                                ))}
                                            </Fieldset>
                                        </div>
                                    </div>
                                    <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                        <span style={{ width: '100%', fontWeight: 'bold', paddingLeft: '4px', paddingBottom: '.25rem' }}>Time Entries</span>
                                        {renderField('Emp #', 'employeeNumberSearch')}
                                        {renderField('Name', 'nameSearch', 'text', '16%')}
                                        {renderField('TkPrs', 'turnkeyProspectsSearch')}
                                        {renderField('Aprs', 'approvalCountSearch')}
                                        {renderField('Aprd', 'approvedCountSearch')}
                                        {renderField('NApr', 'approvalsNeededSearch')}
                                        {renderField('Days', 'daysSearch')}
                                    </div>
                                    <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                        {renderField('Last Date', 'lastDateSearch', 'date')}
                                        {renderField('Inv. Date', 'invoiceDateSearch', 'date')}
                                        {renderField('Proof Date', 'proofDateSearch', 'date')}
                                        {renderField('File Date', 'fileDateSearch', 'date')}
                                        {renderField('Check Date', 'checkDateSearch', 'date')}
                                        {renderField('Receipts', 'receiptsSearch')}
                                        {renderField('Services', 'servicesSearch')}
                                        {renderField('Expenses', 'expensesSearch')}
                                        {renderField('Total', 'totalSearch')}
                                    </div>
                                </form>
                            </Panel>
                        </Panel>
                        </div>
                    </Page.Section>
                </Page>
            </div>
        </div>
    );
};

export default TimeAndExpenseChecklistSearch;



// WEBPACK FOOTER //
// ./src/components/project-billing/components/TimeAndExpenseChecklistSearch.tsx